/**
 * Pago Transbank OneClick con tarjeta ya inscrita.
 * No hay CVV, no hay tokenización en frontend: solo paymentMethodId → backend.
 */

import { useRef } from 'react';
import { useProcessPayment, useProcessSubscriptionBatchPay } from '../hooks/useBilling';
import type { PaymentAttemptResponse, PaymentMethodResponse } from '../../application/dto/BillingDTO';
import { Button } from '@/components/ui/button';
import { getErrorMessage } from '@shared/infrastructure/http/api.error';
import { toast } from 'sonner';

export type PayWithSavedCardProps = {
  /** Una suscripción (flujo unitario). */
  subscriptionId?: string;
  /** Lote: un solo cobro para varias suscripciones. */
  batchId?: string;
  paymentMethod: PaymentMethodResponse;
  onSuccess?: (attempt: PaymentAttemptResponse) => void;
  className?: string;
};

function toastForAttempt(attempt: PaymentAttemptResponse, opts?: { multiplePets?: boolean }) {
  const s = String(attempt.status).toUpperCase();
  if (s === 'SUCCESS') {
    toast.success(
      opts?.multiplePets
        ? 'Pago exitoso, tus mascotas quedaron cubiertas'
        : 'Pago exitoso, tu mascota está cubierta',
    );
    return;
  }
  if (s === 'FAILED') {
    toast.error(attempt.errorMessage ?? 'Pago rechazado, intenta de nuevo');
    return;
  }
  if (s === 'PENDING') {
    toast.info('Pago en proceso, te notificaremos');
    return;
  }
  toast.success('Pago registrado');
}

export function PayWithSavedCard({
  subscriptionId,
  batchId,
  paymentMethod,
  onSuccess,
  className,
}: PayWithSavedCardProps) {
  const processPay = useProcessPayment(subscriptionId);
  const processBatchPay = useProcessSubscriptionBatchPay();
  const isBatch = Boolean(batchId);
  const isPending = processPay.isPending || processBatchPay.isPending;
  const payingRef = useRef(false);

  const brand = paymentMethod.cardBrand ?? 'Tarjeta';
  const last4 = paymentMethod.lastFour ?? paymentMethod.last4 ?? '****';

  const handlePay = async () => {
    if (payingRef.current || isPending) return;
    if (!subscriptionId && !batchId) {
      toast.error('Falta subscriptionId o batchId para procesar el pago.');
      return;
    }
    payingRef.current = true;
    try {
      const idempotencyKey = crypto.randomUUID();
      const attempt = batchId
        ? await processBatchPay.mutateAsync({
            batchId,
            paymentMethodId: paymentMethod.id,
            idempotencyKey,
          })
        : await processPay.mutateAsync({
            subscriptionId: subscriptionId!,
            paymentMethodId: paymentMethod.id,
            idempotencyKey,
          });
      toastForAttempt(attempt, { multiplePets: isBatch });

      const st = String(attempt.status).toUpperCase();
      if (st === 'SUCCESS') {
        onSuccess?.(attempt);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      payingRef.current = false;
    }
  };

  return (
    <div className={`space-y-4 ${className ?? ''}`}>
      <div className="bg-gray-50 rounded-xl px-4 py-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center text-gray-500">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
            />
          </svg>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-800">
            Pagar con <span className="font-semibold">{brand}</span> ····{last4}
          </p>
          <p className="text-xs text-gray-400">
            Cobro seguro vía Transbank OneClick
          </p>
        </div>
      </div>

      <Button
        type="button"
        className="w-full bg-[#FF6F61] hover:bg-[#FF6F61]/90 text-white"
        onClick={handlePay}
        disabled={isPending}
      >
        {isPending ? 'Procesando pago...' : `Pagar con ${brand} ····${last4}`}
      </Button>
    </div>
  );
}
